/**
 * Color Lab essences. Every colour carries a glyph mark (GPT art) so it can
 * be told apart without relying on hue; two colours never share a mark.
 */
export interface LabColor {
  readonly id: string;
  readonly name: string;
  /** Liquid colour, and a lighter tint for the gloss band. */
  readonly hex: string;
  readonly light: string;
  readonly mark: string;
  /** For near shades (chapter "Sắc độ"): the base colour this one is close to. */
  readonly shadeOf?: string;
}

export const LAB_COLORS: readonly LabColor[] = [
  { id: "ruby", name: "Hồng ngọc", hex: "#e8465a", light: "#ff8d9a", mark: "lab.mark-heart" },
  { id: "honey", name: "Mật ong", hex: "#ffb92e", light: "#ffe08a", mark: "lab.mark-star" },
  { id: "sky", name: "Xanh trời", hex: "#3a8cff", light: "#8cc2ff", mark: "lab.mark-drop" },
  { id: "jade", name: "Ngọc bích", hex: "#1fb582", light: "#7fe3bd", mark: "lab.mark-leaf" },
  { id: "amethyst", name: "Thạch anh tím", hex: "#9560f0", light: "#c9a8ff", mark: "lab.mark-moon" },
  { id: "coral", name: "San hô", hex: "#ff7d45", light: "#ffbb93", mark: "lab.mark-flower" },
  { id: "pearl", name: "Ngọc trai", hex: "#e9e2f5", light: "#ffffff", mark: "lab.mark-dot" },
  { id: "teal", name: "Lam ngọc", hex: "#159fae", light: "#7fd7df", mark: "lab.mark-diamond" },
  // Near shades: each sits close to one base colour and only meets it in "Sắc độ" and the finales.
  { id: "rose", name: "Hồng phấn", hex: "#ff8fbd", light: "#ffc6de", mark: "lab.mark-bell", shadeOf: "ruby" },
  { id: "sapphire", name: "Xanh biển", hex: "#2a4bd0", light: "#7d95f0", mark: "lab.mark-snow", shadeOf: "sky" },
  { id: "lime", name: "Xanh cốm", hex: "#97cf2e", light: "#cdee8e", mark: "lab.mark-bolt", shadeOf: "jade" },
  { id: "lilac", name: "Oải hương", hex: "#c99df5", light: "#e7d2ff", mark: "lab.mark-cloud", shadeOf: "amethyst" },
  { id: "mint", name: "Bạc hà", hex: "#5edcc0", light: "#b0f2e2", mark: "lab.mark-shell", shadeOf: "teal" },
  { id: "amber", name: "Hổ phách", hex: "#df8a12", light: "#f6c06a", mark: "lab.mark-sun", shadeOf: "honey" },
  { id: "plum", name: "Mận chín", hex: "#8e2f7c", light: "#d27cc0", mark: "lab.mark-crown", shadeOf: "amethyst" },
  { id: "peach", name: "Đào tiên", hex: "#ffae86", light: "#ffd8c4", mark: "lab.mark-note", shadeOf: "coral" },
];

export const LAB_COLOR_BY_ID: ReadonlyMap<string, LabColor> = new Map(LAB_COLORS.map((color) => [color.id, color]));

/** The colours a level without near shades draws from. */
export const LAB_BASE_COLORS: readonly string[] = LAB_COLORS.filter((color) => !color.shadeOf).map((color) => color.id);

/**
 * What the mixing flask knows. Always the same three recipes, so players
 * learn them once: two primaries make a secondary.
 */
export const LAB_MIXES = [
  { a: "ruby", b: "honey", result: "coral" },
  { a: "honey", b: "sky", result: "jade" },
  { a: "ruby", b: "sky", result: "amethyst" },
] as const;

/** Art the Color Lab scene needs besides the colour marks. */
export const LAB_ASSET_IDS = ["lab.vial", "lab.cork", "lab.seal", "lab.rack", "lab.mist", "lab.flask", "lab.splash", "lab.glow", "reward.sparkle"] as const;
